/**
 * Owner Portal API client
 * Handles authentication and dashboard API calls with cookie-based sessions
 */

/**
 * Fetches with credentials for cookie-based auth
 */
async function ownerFetch(url, options = {}) {
  // RUNTIME API BASE LOGGING (always log on first call to identify cross-domain issues)
  if (typeof window !== 'undefined' && !window.__OWNER_API_BASE_LOGGED) {
    window.__OWNER_API_BASE_LOGGED = true;
    const origin = window.location.origin;
    const fullUrl = url.startsWith('http') ? url : `${origin}${url}`;
    console.log('[OWNER] API_BASE=', url.startsWith('http') ? new URL(url).origin : origin);
    console.log('[OWNER] ORIGIN=', origin);
    console.log('[OWNER] Full URL=', fullUrl);
    console.log('[OWNER] Relative URL=', url);
  }

  // TEMP DEBUG: Check for ?api_debug=1
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  const apiDebug = urlParams?.get('api_debug') === '1';
  
  // RUNTIME GUARD: Block any attempt to call admin endpoints from owner portal
  if (typeof window !== 'undefined' && url.includes('/api/v1/admin/')) {
    const error = new Error(`[OWNER PORTAL GUARD] Blocked attempt to call admin endpoint: ${url}. Owner portal must only use /api/v1/owner/* endpoints.`);
    console.error(error.message);
    throw error;
  }
  
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Ensure credentials are always included
  const fetchOptions = {
    ...options,
    headers,
    credentials: 'include', // Required for cookies
    // Pass through AbortSignal if provided (for request cancellation)
    ...(options.signal && { signal: options.signal }),
  };
  
  if (apiDebug) {
    console.log('[ownerApi] DEBUG: Request', {
      url,
      method: options.method || 'GET',
      hasCredentials: fetchOptions.credentials === 'include',
      headers: fetchOptions.headers,
    });
  }

  const response = await fetch(url, fetchOptions);
  
  if (apiDebug) {
    console.log('[ownerApi] DEBUG: Response', {
      url,
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      hasSetCookie: response.headers.get('set-cookie') !== null,
    });
  }

  const contentType = response.headers.get('content-type');
  const isJson = contentType && contentType.includes('application/json');
  
  let data;
  if (isJson) {
    data = await response.json().catch(() => ({ message: response.statusText }));
  } else {
    data = { message: response.statusText };
  }
  
  if (apiDebug) {
    console.log('[ownerApi] DEBUG: Response data', {
      url,
      status: response.status,
      outcome: data.outcome,
      errorCode: data.errorCode,
      message: data.message,
      hasData: !!data.data,
      hasItems: !!data.items,
      itemsCount: data.items?.length || data.data?.items?.length || 0,
    });
  }

  if (!response.ok) {
    const error = new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    error.requestId = data.requestId;
    error.outcome = data.outcome;
    error.errorCode = data.errorCode;
    if (data.details != null) error.details = data.details;
    
    if (apiDebug) {
      console.log('[ownerApi] DEBUG: Request failed', {
        url,
        status: response.status,
        errorCode: error.errorCode,
        message: error.message,
      });
    }
    
    // Preserve status code for error handling (e.g., 404 vs 500)
    throw error;
  }

  return data;
}

/**
 * POST /api/v1/owner/auth/login
 * @param {string} email - User email
 * @param {string} password - User password
 * @param {AbortSignal} [signal] - Optional AbortSignal for request cancellation
 */
export async function login(email, password, signal) {
  return ownerFetch('/api/v1/owner/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
    ...(signal && { signal }),
  });
}

/**
 * POST /api/v1/owner/auth/logout
 */
export async function logout() {
  return ownerFetch('/api/v1/owner/auth/logout', {
    method: 'POST',
  });
}

/**
 * GET /api/v1/owner/auth/me
 * @param {AbortSignal} [signal] - Optional AbortSignal for request cancellation
 */
let inFlightBootstrapMe = null;

export function getMe(signal) {
  // Several bootstrap mounts can request /me at once. Share only concurrent
  // unsignaled checks; login's abortable verification keeps its own signal.
  if (signal) {
    return ownerFetch('/api/v1/owner/auth/me', { signal });
  }
  if (!inFlightBootstrapMe) {
    const request = ownerFetch('/api/v1/owner/auth/me');
    inFlightBootstrapMe = request.finally(() => {
      inFlightBootstrapMe = null;
    });
  }
  return inFlightBootstrapMe;
}

/**
 * GET /api/v1/owner/dashboard
 */
export async function getDashboard(queryParams = {}) {
  const params = new URLSearchParams(queryParams);
  const url = `/api/v1/owner/dashboard${params.toString() ? `?${params.toString()}` : ''}`;
  return ownerFetch(url);
}

/**
 * GET /api/v1/owner/properties
 * Get list of properties for owner portal
 * @param {AbortSignal} [signal] - Optional AbortSignal for request cancellation
 */
export async function getProperties(signal) {
  return ownerFetch('/api/v1/owner/properties', {
    ...(signal && { signal }),
  });
}

/**
 * GET /api/v1/owner/reservations
 * Get reservations for a listing
 * @param {Object} params - Query parameters
 * @param {string|number} params.listingId - Hostaway listing ID
 * @param {string} [params.startDate] - Start date (YYYY-MM-DD), optional
 * @param {string} [params.endDate] - End date (YYYY-MM-DD), optional
 * @returns {Promise<Object>} Response with items array and metadata
 */
export async function getReservations({ listingId, startDate, endDate, view, signal }) {
  // Guard: Prevent API call with null/undefined listingId (causes 400 error)
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  
  if (startDate) {
    params.append('start_date', startDate);
  }
  if (endDate) {
    params.append('end_date', endDate);
  }
  if (view) {
    params.append('view', view);
  }

  return ownerFetch(`/api/v1/owner/reservations?${params.toString()}`, {
    ...(signal && { signal }),
  });
}

/**
 * GET /api/v1/owner/reservations/:reservation_id
 * Get reservation detail by ID
 * @param {string|number} reservationId - Reservation ID
 * @returns {Promise<Object>} Response with item object and metadata
 */
export async function getReservationDetail(reservationId) {
  return ownerFetch(`/api/v1/owner/reservations/${reservationId}`);
}

/**
 * GET /api/v1/owner/reservations/:reservation_id/notes
 * Get owner-visible notes for a reservation
 * @param {string|number} reservationId - Reservation ID
 * @returns {Promise<Object>} Response with notes array
 */
export async function getReservationNotes(reservationId) {
  return ownerFetch(`/api/v1/owner/reservations/${reservationId}/notes`);
}

/**
 * GET /api/v1/owner/reservations/:reservationId/reviews
 * Get owner-visible reviews for a reservation (owner portal)
 * @param {string|number} reservationId - Hostaway reservation ID
 * @returns {Promise<Object>} Response with reviews array
 */
export async function getReservationReviews(reservationId) {
  return ownerFetch(`/api/v1/owner/reservations/${reservationId}/reviews`);
}

/**
 * GET /api/v1/owner/links
 * Get links for a reservation or listing (owner portal).
 * Backend requires listing_id (Hostaway) when fetching full links list (not has_files_only).
 * @param {Object} params - Query parameters
 * @param {string|number} params.reservation_id - Reservation ID (optional)
 * @param {string|number} params.listing_id - Hostaway listing ID (required for full list; optional for has_files_only)
 * @param {string} params.clc_listing_id - CLC listing ID (optional, legacy)
 * @param {boolean} params.include_property_links - Include property links (default: true)
 * @returns {Promise<Object>} Response with links array
 */
export async function getReservationLinks({ reservation_id, listing_id, clc_listing_id, include_property_links = true }) {
  const params = new URLSearchParams();
  if (reservation_id) {
    params.append('reservation_id', String(reservation_id));
  }
  if (listing_id != null && listing_id !== '') {
    params.append('listing_id', String(listing_id));
  }
  if (clc_listing_id) {
    params.append('clc_listing_id', clc_listing_id);
  }
  if (include_property_links !== undefined) {
    params.append('include_property_links', String(include_property_links));
  }
  return ownerFetch(`/api/v1/owner/links?${params.toString()}`);
}

/** Fetch a binary owner resource as a Blob (cookie auth). Returns { blob, contentType } or null (204). */
async function ownerFetchBlob(url, options = {}) {
  if (typeof window !== 'undefined' && url.includes('/api/v1/admin/')) {
    throw new Error(`[OWNER PORTAL GUARD] Blocked admin endpoint: ${url}`);
  }
  const response = await fetch(url, { credentials: 'include', ...(options.signal && { signal: options.signal }) });
  if (response.status === 204) return null;
  if (!response.ok) {
    const err = new Error(`HTTP ${response.status}`);
    err.status = response.status;
    throw err;
  }
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('content-type') || blob.type || '' };
}

/**
 * GET /api/v1/owner/dropbox/list — one folder level of the owner's property Dropbox folder.
 * @returns { configured, root, path, entries:[{name,kind,size,modified,rel_path}], cursor, has_more }
 */
export async function getOwnerDropboxList({ listing_id, path = '', cursor = null, signal } = {}) {
  const params = new URLSearchParams({ listing_id: String(listing_id) });
  if (path) params.set('path', path);
  if (cursor) params.set('cursor', cursor);
  const res = await ownerFetch(`/api/v1/owner/dropbox/list?${params.toString()}`, { signal });
  return res?.data ?? res;
}

/**
 * Same-origin proxy URL for a Dropbox file — used directly as a <video src> so
 * the browser streams via HTTP Range (206) instead of buffering the whole file
 * into memory (large videos otherwise fail to open on iOS Safari). Cookie auth
 * carries automatically on same-origin media requests.
 */
export function getOwnerDropboxFileUrl(listing_id, path) {
  const params = new URLSearchParams({ listing_id: String(listing_id), path });
  return `/api/v1/owner/dropbox/file?${params.toString()}`;
}

/** GET /api/v1/owner/dropbox/file — proxied file bytes as a Blob (preview/download). */
export async function getOwnerDropboxFileBlob(listing_id, path, { download = false, signal } = {}) {
  const params = new URLSearchParams({ listing_id: String(listing_id), path });
  if (download) params.set('download', '1');
  return ownerFetchBlob(`/api/v1/owner/dropbox/file?${params.toString()}`, { signal });
}

// --- Shared Help & Insights library (one global Dropbox folder, same for all owners) ---

/** GET /api/v1/owner/help-insights/list — one folder level of the shared Help & Insights library. */
export async function getOwnerHelpInsightsList({ path = '', cursor = null, signal } = {}) {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  if (cursor) params.set('cursor', cursor);
  const qs = params.toString();
  const res = await ownerFetch(`/api/v1/owner/help-insights/list${qs ? `?${qs}` : ''}`, { signal });
  return res?.data ?? res;
}

/** Same-origin proxy URL for a Help & Insights file — used as a streaming <video src>. */
export function getOwnerHelpInsightsFileUrl(path) {
  const params = new URLSearchParams({ path });
  return `/api/v1/owner/help-insights/file?${params.toString()}`;
}

/** GET /api/v1/owner/help-insights/file — proxied file bytes as a Blob (preview/download). */
export async function getOwnerHelpInsightsFileBlob(path, { download = false, signal } = {}) {
  const params = new URLSearchParams({ path });
  if (download) params.set('download', '1');
  return ownerFetchBlob(`/api/v1/owner/help-insights/file?${params.toString()}`, { signal });
}

/** Same-origin URL for a Help & Insights file thumbnail (real Dropbox preview; 204 → <img> falls back). */
export function getOwnerHelpInsightsThumbnailUrl(path) {
  const params = new URLSearchParams({ path });
  return `/api/v1/owner/help-insights/thumbnail?${params.toString()}`;
}

// --- Owner Portal Video Guides (one global Dropbox folder; horizontal carousel) ---

/** GET /api/v1/owner/video-guides/list — the flat list of guide videos. */
export async function getOwnerVideoGuidesList({ path = '', signal } = {}) {
  const params = new URLSearchParams();
  if (path) params.set('path', path);
  const qs = params.toString();
  const res = await ownerFetch(`/api/v1/owner/video-guides/list${qs ? `?${qs}` : ''}`, { signal });
  return res?.data ?? res;
}

/** Same-origin streaming URL for a video guide (used as <video src>; Range/206). */
export function getOwnerVideoGuidesFileUrl(path) {
  const params = new URLSearchParams({ path });
  return `/api/v1/owner/video-guides/file?${params.toString()}`;
}

/** Same-origin URL for a video guide poster thumbnail (204 → <img> falls back). */
export function getOwnerVideoGuidesThumbnailUrl(path) {
  const params = new URLSearchParams({ path });
  return `/api/v1/owner/video-guides/thumbnail?${params.toString()}`;
}

/** GET /api/v1/owner/dropbox/notifications — document, reservation, and cancellation
 * notifications from the last 14 days for the access-vetted selected property. */
export async function getOwnerDropboxNotifications({ listing_id, signal } = {}) {
  const qs = listing_id != null ? `?listing_id=${encodeURIComponent(listing_id)}` : '';
  const res = await ownerFetch(`/api/v1/owner/dropbox/notifications${qs}`, { signal });
  return res?.data ?? res;
}

/** POST /api/v1/owner/dropbox/notifications/ack — toggle a notification's seen state. Returns { ok, seen, unseen_count }. */
export async function ackOwnerNotification(notificationKey, seen = true, listing_id = null) {
  const res = await ownerFetch('/api/v1/owner/dropbox/notifications/ack', {
    method: 'POST',
    body: JSON.stringify({ notification_key: notificationKey, seen, ...(listing_id != null && { listing_id }) }),
  });
  return res?.data ?? res;
}

/**
 * GET /api/v1/owner/links?listing_id=<id>&reservation_id=<id>&has_files_only=true&include_property_links=false
 * Lightweight check for reservation-linked files (owner portal, for Files icon dot).
 * Backend requires listing_id for consistent scoping; omit call when listing_id is missing.
 * @param {string|number} reservationId - Reservation ID
 * @param {string|number|null|undefined} listingId - Hostaway listing ID (required when reservationId present; from booking.listing_id)
 * @returns {Promise<{ hasFiles: boolean }>} Response with hasFiles boolean
 * Changelog: 2026-01-27 owner_reservation_financials_files_icon_fix; 2026-01-31 add listing_id + include_property_links for Files panel contract
 */
export async function getReservationHasFiles(reservationId, listingId) {
  if (!reservationId) {
    return { outcome: 'success', data: { hasFiles: false } };
  }
  if (listingId == null || listingId === '' || String(listingId).trim() === '') {
    return { outcome: 'success', data: { hasFiles: false } };
  }
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  params.append('reservation_id', String(reservationId));
  params.append('has_files_only', 'true');
  params.append('include_property_links', 'false');
  const res = await ownerFetch(`/api/v1/owner/links?${params.toString()}`);
  return res;
}

/**
 * GET /api/v1/owner/links
 * Get property-level links for Quick Links (excludes reservation-specific links)
 * @param {Object} params - Query parameters
 * @param {string|number} params.listingId - Hostaway listing ID (required)
 * @returns {Promise<Object>} Response with links array (property-only, reservation_id IS NULL)
 */
export async function getPropertyLinks({ listingId }) {
  // Guard: Prevent API call with null/undefined listingId
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  
  return ownerFetch(`/api/v1/owner/links?${params.toString()}`);
}

/**
 * GET /api/v1/owner/reservations/calendar-metrics
 * Get calendar summary metrics for a given month
 * @param {Object} params - Query parameters
 * @param {string|number} params.listingId - Hostaway listing ID
 * @param {string} params.monthStart - First day of month (YYYY-MM-DD)
 * @param {string} params.monthEnd - Last day of month (YYYY-MM-DD, inclusive)
 * @returns {Promise<Object>} Response with calendar metrics
 */
export async function getCalendarMetrics({ listingId, monthStart, monthEnd }) {
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  params.append('month_start', monthStart);
  params.append('month_end', monthEnd);

  return ownerFetch(`/api/v1/owner/reservations/calendar-metrics?${params.toString()}`);
}

/**
 * GET /api/v1/owner/reservations/next-7-days-snapshot
 * Get snapshot for next 7 days (check-ins, check-outs, occupancy)
 * @param {Object} params - Query parameters
 * @param {string|number} params.listingId - Hostaway listing ID
 * @returns {Promise<Object>} Response with next 7 days snapshot
 */
export async function getNext7DaysSnapshot({ listingId }) {
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));

  return ownerFetch(`/api/v1/owner/reservations/next-7-days-snapshot?${params.toString()}`);
}

/**
 * GET /api/v1/owner/calendar/availability
 * Get Hostaway availability data for calendar month view overlays
 * @param {Object} params - Query parameters
 * @param {string|number} params.listingId - Hostaway listing ID
 * @param {string} params.start - Start date (YYYY-MM-DD)
 * @param {string} params.end - End date (YYYY-MM-DD, inclusive)
 * @returns {Promise<Object>} Response with days map (date -> {status, nightly_rate, min_nights})
 */
export async function getCalendarAvailability({ listingId, start, end }) {
  // Guard: Prevent API call with null/undefined listingId
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  params.append('start', start);
  params.append('end', end);

  return ownerFetch(`/api/v1/owner/calendar/availability?${params.toString()}`);
}

/**
 * POST /api/v1/owner/owner-stays
 * Create a Hostaway ownerStay reservation from the owner portal.
 */
export async function createOwnerStay({
  listingId,
  arrivalDate,
  departureDate,
  ownerName,
  email,
  phone,
  numberOfGuests,
  note,
}) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }

  return ownerFetch('/api/v1/owner/owner-stays', {
    method: 'POST',
    body: JSON.stringify({
      listing_id: Number(listingId),
      arrival_date: arrivalDate,
      departure_date: departureDate,
      owner_name: ownerName,
      email,
      phone,
      number_of_guests: numberOfGuests,
      note,
    }),
  });
}

/**
 * PATCH /api/v1/owner/owner-stays/:reservation_id
 * Update a Hostaway ownerStay reservation from the owner portal.
 */
export async function updateOwnerStay({
  reservationId,
  listingId,
  arrivalDate,
  departureDate,
  ownerName,
  email,
  phone,
  numberOfGuests,
  note,
}) {
  if (!reservationId || reservationId === 'null' || reservationId === 'undefined') {
    const error = new Error('reservationId is required');
    error.status = 400;
    throw error;
  }
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }

  return ownerFetch(`/api/v1/owner/owner-stays/${encodeURIComponent(String(reservationId))}`, {
    method: 'PATCH',
    body: JSON.stringify({
      listing_id: Number(listingId),
      arrival_date: arrivalDate,
      departure_date: departureDate,
      owner_name: ownerName,
      email,
      phone,
      number_of_guests: numberOfGuests,
      note,
    }),
  });
}

/**
 * DELETE /api/v1/owner/owner-stays/:reservation_id
 * Cancel a Hostaway ownerStay reservation from the owner portal.
 */
export async function cancelOwnerStay({ reservationId, listingId }) {
  if (!reservationId || reservationId === 'null' || reservationId === 'undefined') {
    const error = new Error('reservationId is required');
    error.status = 400;
    throw error;
  }
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }

  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));

  return ownerFetch(`/api/v1/owner/owner-stays/${encodeURIComponent(String(reservationId))}?${params.toString()}`, {
    method: 'DELETE',
  });
}

/**
 * GET /api/v1/owner/analytics
 * Get analytics data (revenue, reservations by channel, KPIs)
 * @param {Object} params - Query parameters
 * @param {string} [params.startDate] - Start date (ISO date string, optional)
 * @param {string} [params.endDate] - End date (ISO date string, optional)
 * @param {string} [params.channel] - Channel code filter (optional, single value). Omit for all channels.
 * @param {string} [params.metrics] - Comma-separated metrics (optional, default: revenue,reservations,kpis)
 * @param {AbortSignal} [params.signal] - AbortSignal for request cancellation (optional)
 * @returns {Promise<Object>} Response with analytics data
 */
export async function getOwnerAnalytics({ listingId, startDate, endDate, channel, metrics, debugAnalytics, signal } = {}) {
  const params = new URLSearchParams();
  
  // listingId is required for property scoping
  if (listingId) {
    params.append('listing_id', listingId);
  }
  if (startDate) {
    params.append('startDate', startDate);
  }
  if (endDate) {
    params.append('endDate', endDate);
  }
  if (channel) {
    params.append('channel', channel);
  }
  if (metrics) {
    params.append('metrics', metrics);
  }
  if (debugAnalytics) {
    params.append('debug_analytics', '1');
  }

  return ownerFetch(`/api/v1/owner/analytics?${params.toString()}`, { signal });
}

/**
 * GET /api/v1/owner/activity
 * Returns EZCare activity timeline for a property (maintenance, cleaning, etc.).
 * Query params: listing_id (required), start (YYYY-MM-DD), end (YYYY-MM-DD).
 * Response: { listing_id, is_mapped, start_date, end_date, activities, count }.
 */
export async function getOwnerActivity({ listingId, start, end, signal }) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (start) params.append('start', start);
  if (end) params.append('end', end);
  return ownerFetch(`/api/v1/owner/activity?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/maintenance/history
 * Returns owner-visible maintenance history for the selected property.
 */
export async function getOwnerMaintenanceHistory({ listingId, limit = 12, signal }) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (limit != null) params.set('limit', String(limit));
  return ownerFetch(`/api/v1/owner/maintenance/history?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/maintenance/invoices
 * Returns owner-visible maintenance invoice and billing rows for the selected property.
 */
export async function getOwnerMaintenanceInvoices({ listingId, limit = 12, signal }) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (limit != null) params.set('limit', String(limit));
  return ownerFetch(`/api/v1/owner/maintenance/invoices?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/maintenance/reports
 * Returns owner-visible published maintenance reports for the selected property.
 */
export async function getOwnerMaintenanceReports({ listingId, limit = 12, signal }) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (limit != null) params.set('limit', String(limit));
  return ownerFetch(`/api/v1/owner/maintenance/reports?${params.toString()}`, { ...(signal && { signal }) });
}

export function getOwnerMaintenanceReportArtifactUrl({ listingId, deliveryId }) {
  const params = new URLSearchParams({ listing_id: String(listingId) });
  return `/api/v1/owner/maintenance/reports/${deliveryId}/artifact?${params.toString()}`;
}

export async function downloadOwnerMaintenanceReport({ listingId, deliveryId, fileName = null }) {
  const url = getOwnerMaintenanceReportArtifactUrl({ listingId, deliveryId });
  const response = await fetch(url, { credentials: 'include' });
  if (!response.ok) {
    let message = `Download failed (${response.status})`;
    try {
      const err = await response.json();
      if (err?.message) message = err.message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const nameMatch = disposition && disposition.match(/filename="?([^";\n]+)"?/);
  const filename = fileName || (nameMatch ? nameMatch[1] : 'clc-maintenance-report.pdf');
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(anchor.href);
}

/**
 * GET /api/v1/owner/financials
 * Main financials endpoint. Supports dual-view Ledger:
 * - mode=payment (default): check-in anchored, whole-reservation totals
 * - mode=accrual: night-prorated from nightly fact
 * Supports group_by: 'reservation' (default) or 'guest'.
 */
export async function getFinancials({ listingId, startDate, endDate, groupBy = 'reservation', mode = 'payment', signal }) {
  if (!listingId || listingId === 'null' || listingId === 'undefined') {
    const error = new Error('listingId is required');
    error.status = 400;
    throw error;
  }
  const params = new URLSearchParams();
  params.append('listing_id', String(listingId));
  params.append('start_date', startDate);
  params.append('end_date', endDate);
  if (groupBy) params.append('group_by', groupBy);
  if (mode) params.append('mode', mode);
  return ownerFetch(`/api/v1/owner/financials?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/financials/summary?month=YYYY-MM&listing_id=...
 * Month-ledger totals (cards). Required for Financials page.
 */
export async function getFinancialsSummary({ listingId, month, startDate, endDate, signal }) {
  if (!listingId) throw new Error('listingId is required');
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    params.append('month', month);
  } else if (startDate && endDate) {
    params.append('start_date', startDate);
    params.append('end_date', endDate);
  } else {
    throw new Error('Either month (YYYY-MM) or startDate/endDate are required');
  }
  return ownerFetch(`/api/v1/owner/financials/summary?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/financials/months-with-data?listing_id=...
 * Returns month_keys that have ledger rows for the listing. Used for "Jump to month with activity".
 */
export async function getFinancialsMonthsWithData({ listingId, limit = 24, signal }) {
  if (!listingId) throw new Error('listingId is required');
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (limit != null) params.set('limit', String(limit));
  return ownerFetch(`/api/v1/owner/financials/months-with-data?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/financials/ledger
 * Ledger rows from nightly fact table.
 * Supports month (backward compat) or start_date/end_date, and group_by parameter.
 */
export async function getFinancialsLedger({ listingId, month, startDate, endDate, groupBy = 'reservation', signal }) {
  if (!listingId) throw new Error('listingId is required');
  const params = new URLSearchParams({ listing_id: String(listingId) });
  if (month && /^\d{4}-\d{2}$/.test(month)) {
    params.append('month', month);
  } else if (startDate && endDate) {
    params.append('start_date', startDate);
    params.append('end_date', endDate);
  } else {
    throw new Error('Either month (YYYY-MM) or startDate/endDate are required');
  }
  if (groupBy) params.append('group_by', groupBy);
  return ownerFetch(`/api/v1/owner/financials/ledger?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * GET /api/v1/owner/financials/payments-taxes
 * Reservation-level payments & taxes from clc_reservation_financials_fresh (check-in anchored).
 * Query params: listing_id, start_date, end_date (YYYY-MM-DD, end exclusive).
 * Returns { data: { rows, summary } } with summary.payments_total, charges_total, net_total, reservation_count, nights_total.
 */
export async function getFinancialsPaymentsTaxes({ listingId, startDate, endDate, signal }) {
  if (!listingId || !startDate || !endDate) {
    throw new Error('listingId, startDate, and endDate are required');
  }
  const params = new URLSearchParams({
    listing_id: String(listingId),
    start_date: startDate,
    end_date: endDate,
  });
  return ownerFetch(`/api/v1/owner/financials/payments-taxes?${params.toString()}`, { ...(signal && { signal }) });
}

/**
 * Download financials export (CSV or XLSX) for a given month (backward compat).
 * Uses GET /api/v1/owner/financials/export.csv or export.xlsx with credentials.
 * @param {string|number} listingId - Hostaway listing ID
 * @param {string} month - YYYY-MM
 * @param {'csv'|'xlsx'} format - 'csv' or 'xlsx'
 */
export async function downloadFinancialsExport(listingId, month, format) {
  if (!listingId || !month || !/^\d{4}-\d{2}$/.test(month)) {
    throw new Error('listingId and month (YYYY-MM) are required');
  }
  const ext = format === 'xlsx' ? 'xlsx' : 'csv';
  const path = `/api/v1/owner/financials/export.${ext}`;
  const params = new URLSearchParams({ month, listing_id: String(listingId) });
  const url = `${path}?${params.toString()}`;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
  const response = await fetch(fullUrl, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const nameMatch = disposition && disposition.match(/filename="?([^";\n]+)"?/);
  const filename = nameMatch ? nameMatch[1] : `financials-${month}.${ext}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

/**
 * Download financials export for a date range (CSV or XLSX).
 * Uses GET /api/v1/owner/financials/export.csv or export.xlsx with credentials.
 * @param {string|number} listingId - Hostaway listing ID
 * @param {string} startDate - YYYY-MM-DD
 * @param {string} endDate - YYYY-MM-DD (exclusive)
 * @param {'csv'|'xlsx'} format - 'csv' or 'xlsx'
 * @param {'reservation'|'guest'} groupBy - Group by reservation or guest (default: 'reservation')
 */
export async function downloadFinancialsExportRange({ listingId, startDate, endDate, format, groupBy = 'reservation', mode = 'payment' }) {
  if (!listingId || !startDate || !endDate) {
    throw new Error('listingId, startDate, and endDate are required');
  }
  const ext = format === 'xlsx' ? 'xlsx' : 'csv';
  const path = `/api/v1/owner/financials/export.${ext}`;
  const params = new URLSearchParams({
    listing_id: String(listingId),
    start_date: startDate,
    end_date: endDate,
    group_by: groupBy,
    mode: mode,
  });
  const url = `${path}?${params.toString()}`;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
  const response = await fetch(fullUrl, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const nameMatch = disposition && disposition.match(/filename="?([^";\n]+)"?/);
  const viewSuffix = groupBy === 'guest' ? '-guest' : '-reservation';
  const filename = nameMatch ? nameMatch[1] : `financials-${listingId}-${startDate}_to_${endDate}${viewSuffix}.${ext}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

/**
 * Download financials export for a range of months (CSV or XLSX) - LEGACY.
 * Kept for backward compatibility but deprecated in favor of downloadFinancialsExportRange with date ranges.
 * @param {number} listingId - Hostaway listing ID
 * @param {string[]} months - Array of month keys (YYYY-MM)
 * @param {string} format - 'csv' or 'xlsx'
 * @deprecated Use downloadFinancialsExportRange with startDate/endDate instead
 */
export async function downloadFinancialsExportRangeMonths(listingId, months, format) {
  if (!listingId || !Array.isArray(months) || months.length === 0) {
    throw new Error('listingId and months array are required');
  }
  if (!months.every(m => /^\d{4}-\d{2}$/.test(m))) {
    throw new Error('All months must be in YYYY-MM format');
  }
  const ext = format === 'xlsx' ? 'xlsx' : 'csv';
  const path = `/api/v1/owner/financials/export.${ext}`;
  const params = new URLSearchParams({ listing_id: String(listingId) });
  months.forEach(m => params.append('months', m));
  const url = `${path}?${params.toString()}`;
  const base = typeof window !== 'undefined' ? window.location.origin : '';
  const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
  const response = await fetch(fullUrl, { credentials: 'include' });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Export failed: ${response.status}`);
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const nameMatch = disposition && disposition.match(/filename="?([^";\n]+)"?/);
  const firstMonth = months[0];
  const lastMonth = months[months.length - 1];
  const filename = nameMatch ? nameMatch[1] : `financials-${firstMonth}-to-${lastMonth}.${ext}`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}
